<template>
  <v-card dark color="#2A2A2A" elevation="4" class="acquisition-statistics rounded-xl mt-6" :class="{ 'acquisition-statistics--all': allStudios }">
    <div v-if="!allStudios" class="d-flex align-center flex-wrap px-4 px-md-6 pt-5 pb-4">
      <v-avatar color="rgba(79, 156, 249, 0.12)" size="42" class="mr-3">
        <v-icon color="#7dd3fc">mdi-account-multiple-plus-outline</v-icon>
      </v-avatar>
      <div>
        <h2 class="text-h6 font-weight-bold">Beratung, Probetraining &amp; Gästebuch</h2>
        <p class="text-caption grey--text text--lighten-1 mb-0">
          Kontakte im gewählten Zeitraum und zugehörige Einträge
        </p>
      </div>
      <v-spacer></v-spacer>
      <v-btn v-if="canOpenManagement" :to="{ name: 'homeDashboardAcquisitionManagement' }" outlined small color="#7dd3fc" class="mt-2 mr-3">
        <v-icon left small>mdi-domain</v-icon>Alle Studios öffnen
      </v-btn>
      <span v-if="result && !loading && !error" class="text-caption grey--text text--lighten-1 mt-2 ml-sm-3">
        Stand {{ $moment(result.meta.asOf).format("DD.MM.YYYY") }}
      </span>
    </div>

    <div :class="allStudios ? 'acquisition-statistics__compact-filter' : 'px-4 px-md-6 pb-4'">
      <v-switch
        v-model="hideUnlinkedContacts"
        label="Nicht vollständig zugeordnete Kontakte ausblenden"
        color="#7dd3fc"
        class="acquisition-statistics__filter mt-0 pt-0"
        inset dense hide-details
        :disabled="Boolean(openingAppointment)"></v-switch>
      <span v-if="allStudios && result && !loading && !error" class="acquisition-statistics__asof">Stand {{ $moment(result.meta.asOf).format("DD.MM.YYYY") }}</span>
      <p v-if="!allStudios && hideUnlinkedContacts" class="text-caption grey--text text--lighten-1 mt-2 mb-0">Kennzahlen und Terminlisten berücksichtigen nur vollständig zugeordnete Kontakte.</p>
    </div>

    <div v-if="loading" class="acquisition-statistics__state" role="status" aria-live="polite">
      <v-progress-circular indeterminate size="24" width="3" color="#7dd3fc" class="mr-3"></v-progress-circular>
      Kontaktstatistik wird geladen …
    </div>
    <div v-else-if="error" class="acquisition-statistics__state flex-column text-center px-4" role="alert">
      <p class="mb-3">{{ error }}</p>
      <v-btn small rounded color="primary" depressed @click="fetchStatistics">Erneut laden</v-btn>
    </div>
    <template v-else-if="result">
      <table v-if="!allStudios" class="acquisition-statistics__table">
        <caption class="acquisition-statistics__caption">
          Kontakte im gewählten Zeitraum. Verträge und Anamnesen zählen auch, wenn sie bereits vor dem Kontakt vorhanden waren.
        </caption>
        <thead>
          <tr>
            <th scope="col">Kontakt</th>
            <th scope="col">Anzahl</th>
            <th scope="col">11 Tage aktiviert</th>
            <th scope="col">Vertrag vorhanden</th>
            <th scope="col">Anamnese vorhanden</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in displayRows" :key="row.key">
            <th scope="row" class="acquisition-statistics__source">
              <div class="d-flex align-center">
                <v-icon :color="row.color" size="21" class="mr-3">{{ row.icon }}</v-icon>
                <div>
                  <button type="button" class="acquisition-statistics__source-link font-weight-bold" :disabled="allStudios || !detailsAllowed || !row.total" @click="openDetails(row, 'total')">{{ row.title }}</button>
                  <span class="acquisition-statistics__hint">{{ formatNumber(row.linkedPeople) }} zugeordnete Personen</span>
                  <span v-if="row.unlinkedContacts" class="acquisition-statistics__unlinked">
                    {{ formatNumber(row.unlinkedContacts) }} {{ row.unlinkedContacts === 1 ? "Kontakt" : "Kontakte" }} ohne vollständige Zuordnung
                  </span>
                  <span v-if="row.hiddenContacts" class="acquisition-statistics__hint">
                    {{ formatNumber(row.hiddenContacts) }} {{ row.hiddenContacts === 1 ? "Kontakt ausgeblendet" : "Kontakte ausgeblendet" }}
                  </span>
                </div>
              </div>
            </th>
            <td v-for="metric in detailMetrics" :key="metric.key" :data-label="metric.label" :class="{ 'acquisition-statistics__total': metric.key === 'total' }">
              <v-tooltip bottom :disabled="!metric.beforeContactKey">
                <template v-slot:activator="{ on, attrs }">
                  <span class="acquisition-statistics__count-wrap" v-bind="attrs" v-on="on"
                    :tabindex="metric.beforeContactKey && (!detailsAllowed || !row[metric.key]) ? 0 : null">
                    <button
                      type="button"
                      class="acquisition-statistics__count"
                      :disabled="allStudios || !detailsAllowed || !row[metric.key]"
                      :aria-label="row.title + ': ' + metric.label + ' – ' + formatNumber(row[metric.key]) + ', Details ansehen. ' + existingOutcomeHint(row[metric.beforeContactKey])"
                      v-on="on" @click="openDetails(row, metric.key)">
                      <strong>{{ formatNumber(row[metric.key]) }}</strong>
                      <span>{{ metric.key === 'total' ? row.unit : 'Personen' }}</span>
                    </button>
                  </span>
                </template>
                <span>{{ existingOutcomeHint(row[metric.beforeContactKey]) }}</span>
              </v-tooltip>
            </td>
          </tr>
        </tbody>
      </table>
      <acquisition-studio-comparison
        v-if="allStudios"
        v-model="comparison"
        :studios="result.studios"
        :details-allowed="detailsAllowed"
        :disabled="Boolean(openingAppointment)"
        @open-details="openDetails($event.row, $event.metric, $event.studio)" />
      <div v-if="!allStudios || !detailsAllowed" class="px-4 px-md-6 py-4 acquisition-statistics__footer">
        <p class="text-caption mb-2" :class="{ 'amber--text text--lighten-3': !detailsAllowed }">
          {{ detailsAllowed ? 'Zahl anklicken, um Namen, Termine und Notizen anzusehen.' : 'Für die Detailansicht bitte höchstens einen Monat (31 Tage) auswählen.' }}
        </p>
        <p v-if="!allStudios" class="text-caption mb-2">Jede Person zählt pro Kontaktart und Studio einmal. Verträge und Anamnesen zählen auch, wenn sie schon vor dem Kontakt vorhanden waren.</p>
        <details v-if="!allStudios" class="text-caption">
          <summary>Zählweise ansehen</summary>
          <p class="mt-3 mb-2">Termine zählen nach Termindatum, inklusive geplanter Termine und No-shows. Stornierte und gelöschte Termine sind ausgeschlossen. Ein Termin mit zwei Personen zählt als ein Termin; beide Personen werden bei den zugehörigen Einträgen berücksichtigt.</p>
          <p class="mb-2">Alle Einträge müssen im selben Center liegen und bis zum angezeigten Stand vorhanden sein. 11 Tage zählen nach Startdatum ab dem ersten berücksichtigten Kontakttag, auch nach Ablauf. Verträge zählen inklusive früherer und noch zu prüfender Verträge; Entwürfe, abgelehnte oder gelöschte Verträge und Erneuerungen sind ausgeschlossen. Anamnesen zählen inklusive früherer Einträge, ohne Ernährungsanamnesen. Der Hover über die Zahl zeigt, wie viele Personen bereits vorher einen passenden Eintrag hatten.</p>
          <p class="mb-2">Gästebucheinträge zählen auch nach Archivierung. Bei wiederholter Abgabe wird der zuletzt gespeicherte Eintrag gezählt. Der daraus erzeugte Erstgespräch-Termin wird hier nicht zusätzlich als Beratungstermin gezählt.</p>
          <p v-if="hideUnlinkedContacts" class="mb-2">Der Filter schliesst Kontakte aus, sobald eine beteiligte Person nicht eindeutig zugeordnet ist. Der erste verbleibende Kontakttag bestimmt den Beginn für 11 Tage und die Kennzeichnung früherer Einträge.</p>
          <p class="mb-0">Ohne eindeutige Zuordnung zu einer Person lassen sich die zugehörigen Einträge nicht zählen. Eine Person kann in mehreren Zeilen vorkommen; die Zeilen sind deshalb nicht addierbar.</p>
        </details>
      </div>
    </template>

    <v-dialog v-model="detailOpen" max-width="1280" :fullscreen="$vuetify.breakpoint.xsOnly" :persistent="Boolean(openingAppointment)" scrollable>
      <v-card dark color="#252525" class="acquisition-details">
        <v-card-title class="d-flex align-start flex-nowrap px-4 px-md-6 py-4">
          <div>
            <h2 class="text-h6 font-weight-bold">{{ detailTitle }}</h2>
            <div class="text-caption grey--text text--lighten-1 mt-1" v-if="detailSelection">
              {{ $moment(detailSelection.range[0]).format('DD.MM.YYYY') }} – {{ $moment(detailSelection.range[1]).format('DD.MM.YYYY') }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon aria-label="Detailansicht schliessen" :disabled="Boolean(openingAppointment)" @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <div v-if="detailLoading" class="acquisition-statistics__state" role="status">
            <v-progress-circular indeterminate size="24" color="#7dd3fc" class="mr-3"></v-progress-circular>Details werden geladen …
          </div>
          <div v-else-if="detailError" class="acquisition-statistics__state flex-column px-4 text-center" role="alert">
            <p>{{ detailError }}</p>
            <v-btn small rounded color="primary" @click="fetchDetails(detailPage)">Erneut laden</v-btn>
          </div>
          <template v-else-if="detailData">
            <p v-if="detailSelection.hideUnlinkedContacts" class="text-caption px-4 px-md-6 pt-4 mb-0">Nur vollständig zugeordnete Kontakte, wie in der Übersicht.</p>
            <p v-if="detailSelection.metric !== 'total'" class="text-caption px-4 px-md-6 pt-4 mb-0">
              Jede gezählte Person erscheint einmal mit ihrem ersten Kontakt im gewählten Zeitraum.
            </p>
            <p class="text-caption px-4 px-md-6 pt-4 mb-0">
              Frühere Verträge und Anamnesen zählen in der Übersicht mit. 11 Tage zählen weiterhin ab dem ersten berücksichtigten Kontakt · Stand {{ $moment(detailData.meta.asOf).format('DD.MM.YYYY') }}.
              <span v-if="detailSelection.metric === 'total'">Bei mehreren Personen hat jede eine eigene Zeile.</span>
            </p>
            <v-alert v-if="detailData.changed" type="info" text dense class="ma-4">Einträge wurden inzwischen geändert. Bitte die Liste erneut laden.</v-alert>
            <v-alert v-if="appointmentError" type="error" text dense class="ma-4">{{ appointmentError }}</v-alert>
            <div v-if="!detailData.items.length" class="acquisition-statistics__state px-4 text-center">{{ hideUnlinkedContacts ? 'Keine vollständig zugeordneten Einträge vorhanden.' : 'Keine passenden Einträge vorhanden.' }}</div>
            <div v-else class="acquisition-details__table-wrap">
            <table class="acquisition-details__table">
              <thead><tr><th scope="col">Termin / Eintrag</th><th scope="col">Vorname</th><th scope="col">Nachname</th><th v-for="outcome in detailOutcomeColumns" :key="outcome.key" scope="col">{{ outcome.label }}</th><th scope="col">Notiz</th></tr></thead>
              <tbody>
                <tr v-for="item in detailRows" :key="item.key">
                  <td class="acquisition-details__date">
                    <button v-if="item.appointment" type="button" class="acquisition-details__link" title="Termin in neuem Tab öffnen" :disabled="Boolean(openingAppointment)" @click="openAppointment(item)">
                      {{ $moment(item.date).format('DD.MM.YYYY') }}<span v-if="item.start"> · {{ item.start }}</span>
                      <v-icon size="14" color="inherit" class="ml-1">mdi-open-in-new</v-icon>
                    </button>
                    <span v-else>{{ $moment(item.date).format('DD.MM.YYYY') }}<span v-if="item.start"> · {{ item.start }}</span></span>
                    <span class="acquisition-statistics__hint">{{ item.title }}</span>
                    <span v-if="!item.appointment" class="acquisition-statistics__hint">Kein verknüpfter Termin</span>
                    <v-progress-linear v-if="openingAppointment === item.key" indeterminate color="#7dd3fc" class="mt-2"></v-progress-linear>
                  </td>
                  <td data-label="Vorname">
                    <button v-if="item.appointment" type="button" class="acquisition-details__link" title="Termin in neuem Tab öffnen" :disabled="Boolean(openingAppointment)" @click="openAppointment(item)">{{ item.person.firstName || '–' }}</button>
                    <span v-else>{{ item.person.firstName || '–' }}</span>
                  </td>
                  <td data-label="Nachname">
                    <button v-if="item.appointment" type="button" class="acquisition-details__link" title="Termin in neuem Tab öffnen" :disabled="Boolean(openingAppointment)" @click="openAppointment(item)">{{ item.person.lastName || '–' }}</button>
                    <span v-else>{{ item.person.lastName || '–' }}</span>
                  </td>
                  <td v-for="outcome in item.outcomeCells" :key="outcome.key" :data-label="outcome.label" class="acquisition-details__outcome">
                    <span class="acquisition-details__status" :class="'acquisition-details__status--' + outcome.state" :title="outcome.description">
                      <v-icon size="14" color="inherit" class="mr-1">{{ outcome.icon }}</v-icon>{{ outcome.text }}
                    </span>
                    <small v-if="outcome.beforeContact" class="d-block mt-1">Bereits vorher erstellt</small>
                  </td>
                  <td data-label="Notiz" class="acquisition-details__note">{{ item.note || 'Keine Notiz' }}</td>
                </tr>
              </tbody>
            </table>
            </div>
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-4 py-3 flex-wrap">
          <span class="text-caption grey--text text--lighten-1" v-if="detailData">
            {{ formatNumber(detailData.pagination.total) }} {{ detailSelection.metric === 'total' ? 'Einträge' : 'Personen' }}
            <span v-if="detailData.pagination.hiddenContacts"> · {{ formatNumber(detailData.pagination.hiddenContacts) }} Kontakte ausgeblendet</span>
            · Seite {{ detailData.pagination.page }}
          </span>
          <v-spacer></v-spacer>
          <v-btn text small :disabled="detailLoading || Boolean(openingAppointment) || detailPage <= 1" @click="fetchDetails(detailPage - 1)">Zurück</v-btn>
          <v-btn text small :disabled="detailLoading || Boolean(openingAppointment) || !detailData || !detailData.pagination.hasMore" @click="fetchDetails(detailPage + 1)">Weiter</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"
import socketService from "@/socketService"
import userService from "@/services/userService"
import AcquisitionStudioComparison from "./AcquisitionStudioComparison.vue"
import { defaultComparisonSettings, existingOutcomeHint } from "./acquisitionStudioComparison"

const sources = [
  { key: "consultation", title: "Beratungstermine", unit: "Termine", icon: "mdi-account-tie", color: "#a5b4fc" },
  { key: "trial", title: "Probetrainings", unit: "Termine", icon: "mdi-account-star", color: "#fdba74" },
  { key: "guestbook", title: "Gästebucheinträge", unit: "Einträge", icon: "mdi-book-open-page-variant", color: "#5eead4" },
]
const countKeys = ["total", "linkedPeople", "unlinkedContacts", "hiddenContacts", "elevenDays", "contracts", "medicalHistory", "contractsBeforeContact", "medicalHistoryBeforeContact"]
const validCounts = row => countKeys.every(key => Number.isSafeInteger(row[key]) && row[key] >= 0) &&
  row.contractsBeforeContact <= row.contracts && row.medicalHistoryBeforeContact <= row.medicalHistory
const detailMetrics = [
  { key: "total", label: "Anzahl" }, { key: "elevenDays", label: "11 Tage aktiviert" },
  { key: "contracts", label: "Vertrag vorhanden", beforeContactKey: "contractsBeforeContact" }, { key: "medicalHistory", label: "Anamnese vorhanden", beforeContactKey: "medicalHistoryBeforeContact" },
]
const detailOutcomeColumns = [
  { key: "elevenDays", label: "11 Tage" }, { key: "contracts", label: "Vertrag" }, { key: "medicalHistory", label: "Anamnese" },
]

export default {
  name: "CenterAcquisitionStatistics",
  components: { AcquisitionStudioComparison },
  props: {
    studioId: { type: String, default: "" },
    allStudios: { type: Boolean, default: false },
    range: { type: Array, default: () => [] },
  },
  data: () => ({
    result: null, loading: false, error: "", requestSerial: 0, sources, detailMetrics, detailOutcomeColumns,
    comparison: defaultComparisonSettings(),
    detailOpen: false, detailSelection: null, detailData: null, detailLoading: false, detailError: "", detailPage: 1,
    detailRequestSerial: 0, openingAppointment: "", appointmentError: "", hideUnlinkedContacts: false,
  }),
  computed: {
    requestKey() {
      return JSON.stringify([this.allStudios, this.studioId, this.range[0], this.range[1], this.hideUnlinkedContacts])
    },
    canOpenManagement() {
      return !this.allStudios && userService.hasRight("sm_appointment_management")
    },
    displayRows() {
      return sources.map((source) => ({ ...this.result.rows.find((row) => row.key === source.key), ...source }))
    },
    detailsAllowed() {
      const range = this.result?.meta?.range
      if (!Array.isArray(range) || range.length !== 2) return false
      const days = (Date.parse(`${range[1]}T00:00:00Z`) - Date.parse(`${range[0]}T00:00:00Z`)) / 86400000
      return days >= 0 && days < 31
    },
    detailTitle() {
      if (!this.detailSelection) return "Kontakte"
      return `${this.detailSelection.title} · ${detailMetrics.find((metric) => metric.key === this.detailSelection.metric).label}`
    },
    detailRows() {
      return (this.detailData?.items || []).flatMap((item) => item.people.map((person, index) => ({
        ...item, key: `${item.key}:${index}`, person,
        outcomeCells: detailOutcomeColumns.map((column) => ({ ...column, ...this.outcomeStatus(person.outcomes?.[column.key], person.outcomes?.beforeContact?.[column.key], column.key) })),
      })))
    },
  },
  watch: {
    requestKey: { immediate: true, handler: "fetchStatistics" },
    detailOpen(value) {
      if (!value) this.clearDetails()
    },
  },
  beforeDestroy() {
    this.requestSerial += 1
    this.detailRequestSerial += 1
  },
  methods: {
    existingOutcomeHint,
    formatNumber(value) {
      return Number(value || 0).toLocaleString("de-CH")
    },
    outcomeStatus(value, beforeContact = false, kind = "") {
      if (value === true && beforeContact) return { text: "Ja", state: "yes", icon: "mdi-check-circle-outline", beforeContact: true, description: "Im selben Center bereits vor dem ersten berücksichtigten Kontakt erstellt; zählt in Anzahl und Quote mit." }
      if (value === true) return { text: "Ja", state: "yes", icon: "mdi-check-circle-outline", description: "In dieser Statistik erfasst." }
      if (value === false && ["contracts", "medicalHistory"].includes(kind)) return { text: "Nein", state: "no", icon: "mdi-minus-circle-outline", description: "Kein passender Eintrag im selben Center bis zum angezeigten Stand erfasst, auch nicht vor dem Kontakt." }
      if (value === false) return { text: "Nein", state: "no", icon: "mdi-minus-circle-outline", description: "Keine passende Folgeaktion ab dem ersten berücksichtigten Kontakt erfasst." }
      return { text: "Unklar", state: "unknown", icon: "mdi-help-circle-outline", description: "Keine eindeutige Zuordnung zu einer Person möglich." }
    },
    clearDetails() {
      this.detailRequestSerial += 1
      this.detailData = null
      this.detailLoading = false
      this.detailError = ""
      this.appointmentError = ""
    },
    openDetails(row, metric, studio = null) {
      if (!this.detailsAllowed || !row[metric] || (this.allStudios && !studio?.studioId)) return
      this.clearDetails()
      this.detailSelection = { source: row.key, title: studio ? `${studio.name} · ${row.title}` : row.title, metric, studioId: studio?.studioId || this.studioId, range: [...this.result.meta.range], hideUnlinkedContacts: this.result.meta.hideUnlinkedContacts }
      this.detailOpen = true
      return this.fetchDetails(1)
    },
    async fetchDetails(page = 1) {
      if (!this.detailOpen || !this.detailsAllowed || !this.detailSelection) return
      const request = ++this.detailRequestSerial
      const selected = this.detailSelection
      this.detailPage = page
      this.detailData = null
      this.detailError = ""
      this.appointmentError = ""
      this.detailLoading = true
      try {
        const fetchDetails = this.allStudios ? api.fetchManagementAcquisitionDetails : api.fetchCenterAcquisitionDetails
        const data = await fetchDetails(selected.studioId, selected.range, selected.source, selected.metric, page, selected.hideUnlinkedContacts)
        if (request !== this.detailRequestSerial || !this.detailOpen) return
        if (!Array.isArray(data?.items) || !Number.isInteger(data?.pagination?.total) || !data?.meta?.studioId || data.meta.hideUnlinkedContacts !== selected.hideUnlinkedContacts) throw new Error("Unvollständige Detailansicht")
        this.detailData = data
      } catch (error) {
        if (request !== this.detailRequestSerial || !this.detailOpen) return
        this.detailError = error?.response?.data?.message || "Die Detailansicht konnte nicht geladen werden."
      } finally {
        if (request === this.detailRequestSerial) this.detailLoading = false
      }
    },
    async openAppointment(item) {
      if (!item.appointment?.id || !item.appointment?.date || this.openingAppointment) return
      const targetStudioId = this.detailData?.meta?.studioId
      if (!targetStudioId) return
      this.openingAppointment = item.key
      this.appointmentError = ""
      let appointmentTab = null
      try {
        // Open during the click, before awaiting a studio change, to avoid popup blocking.
        appointmentTab = window.open("about:blank", "_blank")
        if (!appointmentTab) throw new Error("Bitte erlaube Pop-ups für Roody, um den Termin in einem neuen Tab zu öffnen.")
        appointmentTab.opener = null
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}")
        const currentStudioId = this.$store.state.studioStore.selectedStudio?._id || storedUser.user?.studio?._id
        // The calendar loads the session's studio. Apply the existing studio-selection
        // flow before navigating, so statistics for another center open the right day.
        if (String(currentStudioId || "") !== String(targetStudioId)) {
          const studios = await api.fetchAllowedAdminStudios()
          const studio = Array.isArray(studios) && studios.find((entry) => String(entry._id) === String(targetStudioId))
          if (!studio?.code) throw new Error("Für das Center dieses Termins besteht kein Kalenderzugriff.")
          await api.pickStudio({ studio_code: studio.code })
          if (storedUser.user) {
            storedUser.user.studio_id = studio.code
            storedUser.user.studio = studio
            localStorage.setItem("user", JSON.stringify(storedUser))
          }
          await this.$store.dispatch("studioStore/setSelectedStudio", studio)
          if (socketService.socket?.connected) socketService.socket.emit("change_studio", { newStudioId: studio.code })
          await this.$store.dispatch("deviceStore/resetDeviceList")
          sessionStorage.removeItem("homeCourseData")
          sessionStorage.removeItem("homeNewsData")
        }
        if (appointmentTab.closed) throw new Error("Der neue Tab wurde geschlossen. Bitte öffne den Termin erneut.")
        const { href } = this.$router.resolve({ name: "workCalendar", query: { date: item.appointment.date, appointmentId: item.appointment.id } })
        appointmentTab.location.replace(href)
      } catch (error) {
        if (appointmentTab && !appointmentTab.closed) appointmentTab.close()
        this.appointmentError = error?.response?.data?.message || error.message || "Der Termin konnte nicht geöffnet werden."
      } finally {
        this.openingAppointment = ""
      }
    },
    async fetchStatistics() {
      const request = ++this.requestSerial
      this.detailOpen = false
      this.clearDetails()
      this.result = null
      this.error = ""
      this.loading = false
      const start = this.$moment(this.range[0])
      const end = this.$moment(this.range[1])
      if ((!this.allStudios && !this.studioId) || !this.range[0] || !this.range[1] || !start.isValid() || !end.isValid() || start.isAfter(end, "day")) {
        this.error = this.allStudios ? "Bitte einen gültigen Zeitraum auswählen." : "Bitte ein Center und einen gültigen Zeitraum auswählen."
        return
      }
      this.loading = true
      const hideUnlinkedContacts = this.hideUnlinkedContacts
      try {
        const range = [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")]
        const result = this.allStudios
          ? await api.fetchManagementAcquisitionStatistics(range, hideUnlinkedContacts)
          : await api.fetchCenterAcquisitionStatistics(this.studioId, range, hideUnlinkedContacts)
        if (request !== this.requestSerial) return
        if (!result?.meta?.asOf || result.meta.hideUnlinkedContacts !== hideUnlinkedContacts || !Array.isArray(result.rows) || sources.some((source) => !result.rows.some((row) =>
          row.key === source.key && validCounts(row),
        ))) {
          throw new Error("Unvollständige Kontaktstatistik")
        }
        if (this.allStudios && (result.meta.scope !== "all_studios" || !Array.isArray(result.studios) || result.meta.studioCount !== result.studios.length || result.studios.some((studio) =>
          !studio.studioId || typeof studio.name !== "string" || !Array.isArray(studio.rows) || sources.some((source) => !studio.rows.some((row) =>
            row.key === source.key && validCounts(row),
          )),
        ))) throw new Error("Unvollständiger Studiovergleich")
        this.result = result
      } catch (error) {
        if (request !== this.requestSerial) return
        this.error = error?.response?.data?.message || "Die Kontaktstatistik konnte nicht geladen werden."
      } finally {
        if (request === this.requestSerial) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.acquisition-statistics { overflow: hidden; border: 1px solid rgba(148, 163, 184, 0.14); }
.acquisition-statistics--all { margin-top: 12px !important; border-radius: 12px !important; box-shadow: none !important; }
.acquisition-statistics__compact-filter { display: flex; flex-wrap: wrap; align-items: center; gap: 5px 16px; padding: 12px 16px; }
.acquisition-statistics__compact-filter > p { flex-basis: 100%; }
.acquisition-statistics__asof { margin-left: auto; color: #b8c4d4; font-size: 0.75rem; white-space: nowrap; }
.acquisition-statistics--all .acquisition-statistics__footer { padding: 10px 16px !important; }
.acquisition-statistics__count:focus-visible, .acquisition-statistics__source-link:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 2px; }
.acquisition-statistics__state { min-height: 180px; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
.acquisition-statistics__table { width: 100%; border-collapse: collapse; }
.acquisition-statistics__caption { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
.acquisition-statistics__table th, .acquisition-statistics__table td { padding: 18px 20px; text-align: center; }
.acquisition-statistics__table thead { background: rgba(15, 23, 42, 0.3); color: #cbd5e1; font-size: 0.76rem; }
.acquisition-statistics__table thead th:first-child { text-align: left; }
.acquisition-statistics__table tbody tr + tr { border-top: 1px solid rgba(148, 163, 184, 0.12); }
.acquisition-statistics__table .acquisition-statistics__source { text-align: left; font-size: 0.9rem; }
.acquisition-statistics__hint, .acquisition-statistics__unlinked { display: block; margin-top: 4px; font-size: 0.72rem; font-weight: 400; color: #b8c4d4; }
.acquisition-statistics__unlinked { color: #fcd34d; }
.acquisition-statistics__table td strong { display: block; color: #f1f5f9; font-size: 1.6rem; line-height: 1.3; font-variant-numeric: tabular-nums; }
.acquisition-statistics__table td span { font-size: 0.7rem; color: #b8c4d4; }
.acquisition-statistics__table .acquisition-statistics__total strong { color: #7dd3fc; }
.acquisition-statistics__footer { border-top: 1px solid rgba(148, 163, 184, 0.14); color: #b8c4d4; line-height: 1.65; }
.acquisition-statistics__footer summary { cursor: pointer; color: #dbeafe; width: fit-content; padding: 6px 0; }
.acquisition-statistics__footer summary:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; }
.acquisition-statistics__count-wrap { display: inline-block; }
.acquisition-statistics__count-wrap:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 2px; }
.acquisition-statistics__count { min-width: 64px; min-height: 56px; padding: 4px 12px; border-radius: 8px; }
.acquisition-statistics__count:not(:disabled):hover { background: rgba(125, 211, 252, 0.1); }
.acquisition-statistics__source-link { text-align: left; }
.acquisition-statistics__source-link:not(:disabled):hover { text-decoration: underline; text-underline-offset: 4px; }
.acquisition-statistics__count:focus-visible, .acquisition-statistics__source-link:focus-visible, .acquisition-details__link:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; }
.acquisition-details { max-height: 85vh; }
.acquisition-statistics__filter ::v-deep .v-label { font-size: 0.83rem; line-height: 1.4; }
.acquisition-details__table-wrap { overflow-x: auto; }
.acquisition-details__table { width: 100%; min-width: 1040px; border-collapse: collapse; color: #e2e8f0; }
.acquisition-details__table th { color: #b8c4d4; font-size: 0.74rem; background: #252525; position: sticky; top: 0; z-index: 1; }
.acquisition-details__table th, .acquisition-details__table td { padding: 16px; text-align: left; vertical-align: top; }
.acquisition-details__table tbody tr + tr { border-top: 1px solid rgba(148, 163, 184, 0.14); }
.acquisition-details__table td { font-size: 0.83rem; }
.acquisition-details__link { color: #7dd3fc; text-align: left; min-height: 30px; }
.acquisition-details__link:hover { text-decoration: underline; text-underline-offset: 3px; }
.acquisition-details__note { white-space: pre-wrap; overflow-wrap: anywhere; min-width: 180px; max-width: 460px; }
.acquisition-details__date { min-width: 170px; }
.acquisition-details__status { display: inline-flex; align-items: center; min-height: 30px; padding: 3px 8px; border-radius: 7px; font-size: 0.75rem; font-weight: 600; white-space: nowrap; }
.acquisition-details__status--yes { color: #86efac; background: rgba(134, 239, 172, 0.1); }
.acquisition-details__status--no { color: #cbd5e1; background: rgba(148, 163, 184, 0.1); }
.acquisition-details__status--unknown { color: #fde68a; background: rgba(253, 230, 138, 0.1); }
@media (max-width: 959px) {
  .acquisition-statistics__table th, .acquisition-statistics__table td { padding: 16px 12px; }
  .acquisition-details__table-wrap { overflow: visible; }
  .acquisition-details__table, .acquisition-details__table tbody { display: block; min-width: 0; }
  .acquisition-details__table thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
  .acquisition-details__table tr { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); padding: 10px 16px; }
  .acquisition-details__table td { grid-column: span 3; padding: 8px 4px; min-width: 0; }
  .acquisition-details__table .acquisition-details__outcome { grid-column: span 2; }
  .acquisition-details__table .acquisition-details__date, .acquisition-details__table .acquisition-details__note { grid-column: 1 / -1; }
  .acquisition-details__table td[data-label]::before { content: attr(data-label); display: block; margin-bottom: 5px; color: #b8c4d4; font-size: 0.72rem; }
  .acquisition-details__note { max-width: none; }
}
@media (max-width: 599px) {
  .acquisition-details { max-height: 100%; }
  .acquisition-statistics__table thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
  .acquisition-statistics__table, .acquisition-statistics__table tbody { display: block; }
  .acquisition-statistics__table tbody tr { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); padding: 6px 16px 16px; }
  .acquisition-statistics__table .acquisition-statistics__source { grid-column: 1 / -1; padding: 18px 0 12px; }
  .acquisition-statistics__table td { padding: 12px 8px; text-align: left; }
  .acquisition-statistics__table td::before { content: attr(data-label); display: block; margin-bottom: 6px; color: #cbd5e1; font-size: 0.72rem; }
}
</style>
